import React, { useEffect, useState, useCallback } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { stats, marqueeWords } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';

function CountUp({ end, suffix, isVisible, duration = 2000 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const startTime = performance.now();
    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);

  const formatNumber = useCallback((num) => (num >= 1000 ? num.toLocaleString() : num.toString()), []);

  return <span>{formatNumber(count)}{suffix}</span>;
}

export default function StatsSection() {
  const { t, tl } = useLanguage();
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <>
      <section className="py-16 sm:py-24 lg:py-32 bg-[#111114] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#c9a96e]/[0.02] via-transparent to-transparent pointer-events-none" />
        <div ref={sectionRef} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 animate-reveal ${isVisible ? 'visible' : ''}`}>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-[#c9a96e] text-xs font-semibold tracking-[0.25em] uppercase">{t('stats_label')}</span>
            <div className="h-px flex-1 bg-[#2a2a30]" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#f0ebe3] mb-10 sm:mb-16">
            {t('stats_h2')} <span className="text-gold-gradient">{t('stats_h2_gold')}</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <div key={stat.id} className={`text-center lg:text-left animate-reveal ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: `${0.2 + i * 0.15}s` }}>
                <p className="text-[#c9a96e] font-heading text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-none stat-number">
                  <CountUp end={stat.value} suffix={stat.suffix} isVisible={isVisible} duration={stat.value > 100 ? 2500 : 2000} />
                </p>
                <p className="text-[#f0ebe3] text-xs sm:text-sm font-medium mt-3 sm:mt-4 mb-1">{tl(stat.label)}</p>
                <p className="text-[#6a6560] text-[11px] sm:text-xs leading-relaxed">{tl(stat.description)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="py-6 sm:py-8 bg-[#0a0a0c] border-y border-[#1a1a1f] overflow-hidden">
        <div className="animate-marquee flex whitespace-nowrap">
          {[...marqueeWords, ...marqueeWords, ...marqueeWords, ...marqueeWords].map((word, i) => (
            <span key={i} className="text-[#1e1e24] font-heading text-3xl sm:text-5xl lg:text-7xl font-bold uppercase mx-4 sm:mx-8 select-none">
              {word}<span className="text-[#c9a96e]/20 mx-3 sm:mx-6">&bull;</span>
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
